import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/auth";
import { KeyRound, Trash2, Users, Loader2, Copy, Cpu, ShieldCheck, Settings, BadgeCheck, ScrollText } from "lucide-react";
import { ManualActivation } from "@/components/ManualActivation";
import { AuditLog } from "@/components/AuditLog";
import { LicenseOrdersAdmin } from "@/components/LicenseOrdersAdmin";
import { useUnseenOrders } from "@/hooks/useUnseenOrders";
import CompatManager from "@/pages/CompatManager";
import HelloWhitelistManager from "@/pages/HelloWhitelistManager";
import ThirdPartyManager from "@/pages/ThirdPartyManager";

const PLANS = ["1M", "3M", "6M", "12M", "LIFETIME"];

const TABS = [
  { id: "users",   label: "Utilisateurs & Licences", icon: Users },
  { id: "orders", label: "Demandes de licences", icon: KeyRound },
  { id: "activation", label: "Licences · Activation", icon: BadgeCheck },
  { id: "audit",   label: "Journal d'audit", icon: ScrollText },
  { id: "compat",  label: "Compatibilité (Vague 2)", icon: Cpu },
  { id: "hello",   label: "Hello Bypass", icon: ShieldCheck },
  { id: "store",   label: "Logiciels tiers", icon: Settings },
];

export default function AdminPage() {
  const [tab, setTab] = useState("users");
  const [users, setUsers] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [plan, setPlan] = useState("1M");
  const [count, setCount] = useState(1);
  const [busy, setBusy] = useState(false);
  const [stats, setStats] = useState(null);

  const load = async () => {
    const [u, l, s] = await Promise.all([
      axios.get(`${API}/users`),
      axios.get(`${API}/licenses`),
      axios.get(`${API}/stats`),
    ]);
    setUsers(u.data); setLicenses(l.data); setStats(s.data);
  };
  useEffect(() => { if (tab === "users") load(); }, [tab]);

  const generate = async () => {
    setBusy(true);
    try { await axios.post(`${API}/licenses/generate`, { plan, count: Number(count) }); await load(); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce compte ?")) return;
    await axios.delete(`${API}/users/${id}`); load();
  };
  const suspend = async (id) => { await axios.post(`${API}/users/${id}/suspend`); load(); };
  const reactivate = async (id) => { await axios.post(`${API}/users/${id}/reactivate`); load(); };
  const copyKey = (k) => navigator.clipboard.writeText(k);

  const unseen = useUnseenOrders();

  return (
    <div className="p-4 sm:p-6 space-y-6" data-testid="admin-page">
      <div className="card-lhs p-5 sm:p-6">
        <div className="text-xs uppercase tracking-widest text-zinc-500 font-mono-tech">Console Admin</div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold mt-1">Administration LHSunlocker</h1>
        <p className="text-sm text-zinc-400 mt-2">Comptes, licences, moteur de compatibilité et whitelist Hello.</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto scroll-thin -mx-1 px-1" data-testid="admin-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg text-sm border transition ${
              tab === t.id
                ? "bg-orange-500/10 text-orange-400 border-orange-500/40 orange-glow"
                : "border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600"
            }`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
            {t.id === "orders" && unseen > 0 && tab !== "orders" && (
              <span data-testid="admin-orders-badge" className="ml-1 min-w-5 h-5 px-1.5 rounded-full bg-orange-500 text-black text-[10px] font-bold inline-flex items-center justify-center">{unseen}</span>
            )}
          </button>
        ))}
      </div>

      {tab === "users" && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <Mini label="Utilisateurs" value={stats?.users_count ?? "—"} />
            <Mini label="Licences actives" value={stats?.active_licenses ?? "—"} color="text-emerald-400" />
            <Mini label="Opérations totales" value={stats?.total ?? 0} />
            <Mini label="Taux succès" value={`${stats?.success_rate ?? 0}%`} color="text-orange-400" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="card-lhs p-5">
              <div className="flex items-center gap-2 mb-4">
                <KeyRound className="w-4 h-4 text-orange-500" />
                <h2 className="font-display font-bold uppercase tracking-wider text-sm">Générateur de licences</h2>
              </div>
              <div className="flex flex-col sm:flex-row gap-2">
                <select data-testid="gen-plan" className="input-lhs sm:w-32" value={plan} onChange={(e) => setPlan(e.target.value)}>
                  {PLANS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <input data-testid="gen-count" type="number" min={1} max={50} value={count} onChange={(e) => setCount(e.target.value)} className="input-lhs sm:w-24" />
                <button data-testid="gen-btn" disabled={busy} onClick={generate} className="btn-orange px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
                  Générer
                </button>
              </div>
              <div className="mt-4 max-h-80 overflow-y-auto scroll-thin space-y-2" data-testid="licenses-list">
                {licenses.map(l => (
                  <div key={l.key} className="flex items-center justify-between rounded-lg border border-zinc-800 bg-[#0C0C10] p-2.5 text-xs gap-2">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                      <span className={`brand-pill shrink-0 ${l.status === "used" ? "text-zinc-500" : "text-emerald-400"}`}>{l.status.toUpperCase()}</span>
                      <span className="font-mono-tech text-orange-400 truncate">{l.key}</span>
                      <span className="text-zinc-500 font-mono-tech shrink-0">{l.plan}</span>
                    </div>
                    <button onClick={() => copyKey(l.key)} className="text-zinc-400 hover:text-orange-400 shrink-0"><Copy className="w-4 h-4" /></button>
                  </div>
                ))}
                {licenses.length === 0 && <div className="text-sm text-zinc-500 italic">Aucune licence générée.</div>}
              </div>
            </div>

            <div className="card-lhs p-5">
              <div className="flex items-center gap-2 mb-4">
                <Users className="w-4 h-4 text-orange-500" />
                <h2 className="font-display font-bold uppercase tracking-wider text-sm">Comptes techniciens</h2>
              </div>
              <div className="max-h-96 overflow-y-auto scroll-thin space-y-2" data-testid="users-list">
                {users.map(u => (
                  <div key={u.id} className="flex flex-col sm:flex-row sm:items-center justify-between rounded-lg border border-zinc-800 bg-[#0C0C10] p-3 text-sm gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{u.name} <span className="text-[10px] text-orange-400 uppercase font-mono-tech ml-1">{u.role}</span></div>
                      <div className="text-[11px] text-zinc-500 font-mono-tech truncate">{u.email} · {u.workshop || "—"}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">Licence: <span className={licenseColor(u.license_status)}>{u.license_status}</span> · {u.license_plan || "—"}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {u.license_status === "suspended" ? (
                        <button title="Réactiver" onClick={() => reactivate(u.id)} className="text-emerald-400 hover:text-emerald-300 text-xs px-2 py-1 border border-emerald-500/30 rounded" data-testid={`reactivate-${u.id}`}>Réactiver</button>
                      ) : u.license_status === "active" ? (
                        <button title="Suspendre" onClick={() => suspend(u.id)} className="text-amber-400 hover:text-amber-300 text-xs px-2 py-1 border border-amber-500/30 rounded" data-testid={`suspend-${u.id}`}>Suspendre</button>
                      ) : null}
                      <button onClick={() => del(u.id)} className="text-zinc-500 hover:text-red-400" data-testid={`del-user-${u.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>
      )}

      {tab === "orders" && <LicenseOrdersAdmin />}
      {tab === "activation" && <ManualActivation />}
      {tab === "audit" && <AuditLog />}
      {tab === "compat" && <CompatManager />}
      {tab === "hello" && <HelloWhitelistManager />}
      {tab === "store" && <ThirdPartyManager />}
    </div>
  );
}

function licenseColor(status) {
  if (status === "active") return "text-emerald-400";
  if (status === "suspended") return "text-amber-400";
  return "text-red-400";
}

function Mini({ label, value, color = "text-white" }) {
  return (
    <div className="card-lhs p-4">
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 font-mono-tech">{label}</div>
      <div className={`font-display text-2xl sm:text-3xl font-extrabold mt-2 ${color}`}>{value}</div>
    </div>
  );
}
